import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { EChartsOption } from 'echarts';
import {
  SparkChartMetric,
  SparkTimeseriesDto,
} from '@/app/common/general-types/spark-ads-v3.dto';
import { EchartGenericChartComponent } from '@/app/charts/echart-generic-chart/echart-generic-chart.component';
import { SparkLoadState } from '../../../../spark-ads.types';
import { formatCurrency, formatShortNumber } from '../../../../spark-ads.format';

interface MetricOption {
  key: SparkChartMetric;
  label: string;
  note: string;
}

/**
 * Daily performance, with a segmented control to switch metric.
 *
 * Rendered through the shared ECharts wrapper using its pink bar variant, so the bars
 * pick up the Studio accent and the app's own axis/tooltip theming.
 */
@Component({
  selector: 'sa-daily-performance-chart',
  imports: [EchartGenericChartComponent],
  templateUrl: './daily-performance-chart.html',
  styleUrl: './daily-performance-chart.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DailyPerformanceChart {
  readonly series = input<SparkTimeseriesDto | undefined>(undefined);
  readonly metric = input.required<SparkChartMetric>();
  readonly loadState = input.required<SparkLoadState>();
  readonly currencyCode = input<string | null>(null);

  readonly metricChange = output<SparkChartMetric>();

  protected readonly metricOptions: readonly MetricOption[] = [
    {
      key: 'spend',
      label: 'Spend',
      note: 'Daily spend across every live ad group',
    },
    {
      key: 'reach',
      label: 'Reach',
      note: 'Daily unique reach',
    },
    {
      key: 'conversions',
      label: 'Conversions',
      note: 'Daily attributed conversions',
    },
  ];

  protected readonly note = computed(
    () => this.metricOptions.find((option) => option.key === this.metric())?.note ?? '',
  );

  protected readonly points = computed(() => this.series()?.points ?? []);

  protected readonly hasData = computed(() =>
    this.points().some((point) => point.value > 0),
  );

  /** Day-of-month labels, matching the design's compact axis. */
  protected readonly axisLabels = computed(() =>
    this.points().map((point) => String(Number(point.date.slice(8, 10)))),
  );

  protected readonly chartOption = computed<EChartsOption>(() => {
    const values = this.points().map((point) => point.value);
    const metric = this.metric();
    const currencyCode = this.currencyCode();

    return {
      grid: { left: 8, right: 8, top: 16, bottom: 8, containLabel: true },
      xAxis: { type: 'category', data: this.axisLabels() },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) =>
            metric === 'spend' ? formatCurrency(value, currencyCode, 0) : formatShortNumber(value),
        },
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (value) =>
          metric === 'spend'
            ? formatCurrency(Number(value), currencyCode, 2)
            : formatShortNumber(Number(value)),
      },
      series: [
        {
          type: 'bar',
          data: values,
          barMaxWidth: 28,
          itemStyle: { borderRadius: [6, 6, 2, 2] },
        },
      ],
    };
  });

  protected selectMetric(metric: SparkChartMetric): void {
    this.metricChange.emit(metric);
  }

  protected getSegmentClass(metric: SparkChartMetric): string {
    return this.metric() === metric
      ? 'bg-card text-primary shadow-sm'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }
}
